import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import clsx from "clsx"
import { useMemo } from "react"

import useIsDesktop from "@hooks/useIsDesktop"

import { faIcon } from "@utils/faIcon"
import navigatorVibrate from "@utils/navigatorVibrate"

import { Hint, Icon, IconButton } from "@atoms"

import useTable from "@organisms/Table/Table.context"

import styles from "./TableViewMenuColumnOrderingItem.module.scss"
import { TableViewMenuColumnOrderingItemProps } from "./TableViewMenuColumnOrderingItem.types"

export default function TableViewMenuColumnOrderingItem(props: TableViewMenuColumnOrderingItemProps) {
    const { columnId, isTabbable, onPinColumn, onUnpinColumn, isPinned } = props

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: columnId })

    const isDesktop = useIsDesktop()

    const { updateColumnVisibility, reachedPinLimit, reachedMinimumOfColumnsVisible, columns } = useTable()

    const handleColumnVisibilityChange = () => {
        updateColumnVisibility(columnId, false)
    }

    const handleColumnPinningChange = () => {
        if (isPinned) {
            onUnpinColumn(columnId)
        } else {
            onPinColumn(columnId)
        }
    }

    const vibrateUserPhoneIfReachingPinLimit = () => {
        if (reachedPinLimit && !isPinned) {
            navigatorVibrate()
        }
    }

    const label = useMemo(() => {
        const column = columns.find((col) => "accessorKey" in col && col.accessorKey === columnId)
        return column?.header as string
    }, [columnId, columns])

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        touchAction: "none",
    }

    const properIcon = useMemo(() => {
        if (isDragging) {
            return faIcon({ name: "arrows-up-down", style: "regular" })
        } else {
            return faIcon({ name: "grip-lines", style: "regular" })
        }
    }, [isDragging])

    return (
        <>
            <div
                className={clsx(styles.base, {
                    [styles.reachedPinLimit]: reachedPinLimit,
                    [styles.dragging]: isDragging,
                })}
                style={style}
                ref={setNodeRef}
                {...attributes}
                {...listeners}
                tabIndex={-1}
            >
                <div className={styles.container}>
                    <div className={styles.iconAndLabel}>
                        <div className={styles.icon}>
                            <Icon icon={properIcon} size={16} />
                        </div>
                        <div className={styles.label}>{label}</div>
                    </div>
                    <Hint
                        colorScheme="tomato"
                        title="Pinned Columns Limit"
                        body="Maximum number of pinned columns is 3. Unpin one of the columns to pin a new one."
                        defaultPlacement="left"
                        isDisabled={!(reachedPinLimit && !isPinned)}
                    >
                        <div className={styles.buttons} data-no-dnd="true">
                            <div onTouchStart={vibrateUserPhoneIfReachingPinLimit}>
                                <IconButton
                                    size={isDesktop ? "lg" : "xl"}
                                    variant="ghost"
                                    className={isPinned ? styles.unpinButton : styles.pinButton}
                                    isTabbable={isTabbable}
                                    isDisabled={reachedPinLimit && !isPinned}
                                    icon={faIcon({ name: "thumbtack", style: "regular" })}
                                    iconOnActive={faIcon({ name: "regular-thumbtack-slash", style: "kit" })}
                                    iconIsActive={isPinned}
                                    onClick={handleColumnPinningChange}
                                    noDebounce={true}
                                />
                            </div>
                        </div>
                    </Hint>
                    <IconButton
                        size={isDesktop ? "lg" : "xl"}
                        variant="ghost"
                        isTabbable={isTabbable}
                        isDisabled={reachedMinimumOfColumnsVisible}
                        icon={faIcon({ name: "xmark", style: "regular" })}
                        onClick={handleColumnVisibilityChange}
                        noDebounce={true}
                    />
                </div>
            </div>
        </>
    )
}
